import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableRow,
} from '@mui/material';
import dayjs from 'dayjs';
import { memo, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CloseIcon, InformationFillIcon, StarIcon } from '~/assets/icons';
import BaseRow from '~/components/common/base-row/BaseRow';
import { cast, handleApiError } from '~/utils/helper';
import { IAccount } from '~/utils/interface/account';
import accountApi from '~/apis/account';
import { ROLE_TECHINICIAN } from '~/utils/constants/account';
import { BaseScrollbar } from '~/components/common';
import ShowUserDialog from '../../users/component/show-user-dialog/ShowUserDialog';
import { useAppDispatch, useAppSelector } from '~/redux/hooks';
import { handleGetAuthUserInfo } from '~/thunks/auth/authThunk';

type DetailTechnicianDialogProps = {
  open: boolean;
  setOpen?: (open: boolean, needRefreshData?: boolean) => void;
  data?: { username: string };
  parent_username?: string;
};

const DetailTechnicianDialog = (props: DetailTechnicianDialogProps) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation('admin/technician');

  const { authUser } = useAppSelector((state) => state.auth);

  const [technician, setTechnician] = useState<IAccount>();
  const [technicians, setTechnicians] = useState<IAccount[]>();

  const [isOpenDetailTechnicianDialog, setIsOpenDetailTechnicianDialog] = useState<boolean>(false);

  const dataDetail = useRef<any>();
  const roleType = useRef<string>();

  useEffect(() => {
    if (props.open && props.data) {
      (async () => {
        try {
          const response = await accountApi.getByRoleList(ROLE_TECHINICIAN, { username: props.data?.username });
          if (response.data?.data?.length) {
            setTechnician(response.data.data[0]);
          }
        } catch (error) {
          handleApiError(error);
        }
      })();

      (async () => {
        try {
          const response = await accountApi.getByRole({ role: ROLE_TECHINICIAN, username: props.data?.username });
          setTechnicians(response.data);
        } catch (error) {
          handleApiError(error);
        }
      })();

      dispatch(handleGetAuthUserInfo());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.open]);

  const handleSetOpen = (open: boolean, needRefreshData?: boolean) => {
    if (props.setOpen) {
      props.setOpen(open, needRefreshData);
    }
  };

  const getRatingStatus = (status: number) => {
    if (status === 1) {
      return <Box className='text-[#ED1002]'>{t('ratingStatus.improvement')}</Box>;
    }
    if (status === 2) {
      return <Box className='text-[#3D9149]'>{t('ratingStatus.meetRequirement')}</Box>;
    }
    if (status === 3) {
      return <Box className='text-[#3D9149]'>{t('ratingStatus.excellence')}</Box>;
    }
    return '';
  };

  const getParentUserName = () => {
    if (props?.parent_username) return props?.parent_username;
    else if (technician?.parent_username) return technician?.parent_username;
    return authUser.username;
  };

  return (
    <>
      <Dialog open={props.open} onClose={() => handleSetOpen(false)} fullWidth maxWidth='sm'>
        <DialogTitle className='bg-[#005F73]'>
          {t('detailTechnicianDialog.title')}
          <IconButton onClick={() => handleSetOpen(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <BaseRow>
            <Box className='w-full relative'>
              <Box className='flex flex-row gap-3.5 mb-4 justify-between'>
                <Box className='font-bold'>{t('detailTechnicianDialog.name')}:</Box>
                <Box className='font-semibold'>{technician?.name}</Box>
              </Box>
              <Box className='flex flex-row gap-3.5 mb-4 justify-between'>
                <Box className='font-bold'>{t('detailTechnicianDialog.address')}:</Box>
                <Box className='font-semibold'>{technician?.address}</Box>
              </Box>
              <Box className='flex flex-row gap-3.5 mb-4 justify-between'>
                <Box className='font-bold'>{t('detailTechnicianDialog.phone')}:</Box>
                <Box className='font-semibold'>{technician?.phone}</Box>
              </Box>
              <Box className='flex flex-row gap-3.5 mb-4 justify-between'>
                <Box className='font-bold'>{t('detailTechnicianDialog.email')}:</Box>
                <Box className='font-semibold'>{technician?.email}</Box>
              </Box>
              <Box className='flex flex-row gap-3.5 mb-4 justify-between'>
                <Box className='font-bold'>{t('detailTechnicianDialog.parentUsername')}:</Box>
                <Box className='font-semibold'>{getParentUserName()}</Box>
              </Box>
              <Box className='flex flex-row gap-3.5 mb-4 justify-between'>
                <Box className='font-bold'>{t('detailTechnicianDialog.createdAt')}:</Box>
                <Box className='font-semibold'>
                  {technician?.created_at ? dayjs(technician.created_at).format('DD/MM/YYYY') : ''}
                </Box>
              </Box>
              <Box className='flex flex-row gap-3.5 mb-4 justify-between items-center'>
                <Box className='font-bold'>{t('detailTechnicianDialog.totalRatings')}:</Box>
                <Box className='font-semibold flex justify-center items-center'>
                  {technician?.total_ratings}<StarIcon fill='#FF9500' className='w-[18px] h-[18px]' />
                </Box>
              </Box>
              <Box className='flex flex-row gap-3.5 mb-4 justify-between'>
                <Box className='font-bold'>{t('detailTechnicianDialog.ratingStatus')}:</Box>
                <Box className='font-semibold'>{getRatingStatus(cast(technician?.rating_status))}</Box>
              </Box>
              <Box className='mt-5'>
                <BaseRow className='mb-[13px] gap-3.5 w-full !justify-between !items-center'>
                  <Box className='font-bold text-[#005F73] text-[18px]'>
                    {t('detailTechnicianDialog.technicianList')}
                  </Box>
                  <Box className='rounded-full text-white w-[25px] h-[25px] flex items-center justify-center font-bold bg-[#005F73] p-2'>
                    {technicians?.length}
                  </Box>{' '}
                </BaseRow>
                {technicians?.length ? (
                  <BaseScrollbar maxHeight='300px'>
                    <Box className='rounded-[10px] overflow-hidden border-[1px] border-[#005F73]'>
                      <Table>
                        <TableBody className='divide-y divide-[#005F73]'>
                          {technicians.map((row, index) => {
                            return (
                              <TableRow key={index}>
                                <TableCell className='border-none'>
                                  <Box>
                                    <Box className='font-bold'>{row.username}</Box>
                                    <Box className='mb-[ 5px]'>{row.name}</Box>
                                  </Box>
                                </TableCell>
                                <TableCell className='border-none w-[80px]'>
                                  <Box className='flex'>
                                    <IconButton
                                      onClick={() => {
                                        dataDetail.current = row;
                                        roleType.current = ROLE_TECHINICIAN;
                                        setIsOpenDetailTechnicianDialog(true);
                                      }}
                                    >
                                      <InformationFillIcon fill='#D9D9D9' />
                                    </IconButton>
                                  </Box>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </Box>
                  </BaseScrollbar>
                ) : null}
              </Box>
            </Box>
          </BaseRow>
        </DialogContent>
      </Dialog>

      {isOpenDetailTechnicianDialog ? (
        <ShowUserDialog
          open={isOpenDetailTechnicianDialog}
          setOpen={setIsOpenDetailTechnicianDialog}
          user={dataDetail.current}
        />
      ) : null}
    </>
  );
};

export default memo(DetailTechnicianDialog);
